import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, Plus, Edit2, Trash2, RefreshCw, ArrowLeft, MessageSquare,
  UserPlus, Target, Clock, Globe, Settings, Play, Pause, ChevronRight,
  Mail, Phone, User, FileText, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatRules = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('greeting');
  const [loading, setLoading] = useState(true);
  
  // Rules data
  const [greetingRules, setGreetingRules] = useState([]);
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [qualificationRules, setQualificationRules] = useState([]);
  const [offlineRules, setOfflineRules] = useState([]);
  
  // Supporting data
  const [websites, setWebsites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [token]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [greetingRes, assignmentRes, qualificationRes, offlineRes, websitesRes, teamsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/api/live-chat/rules/auto-greeting?token=${token}`),
        axios.get(`${API}/api/live-chat/rules/auto-assignment?token=${token}`),
        axios.get(`${API}/api/live-chat/rules/lead-qualification?token=${token}`),
        axios.get(`${API}/api/live-chat/rules/offline-capture?token=${token}`),
        axios.get(`${API}/api/live-chat/websites?token=${token}&status=active`),
        axios.get(`${API}/api/teams?token=${token}`),
        axios.get(`${API}/api/organization/users?token=${token}`)
      ]);

      if (greetingRes.data.success) setGreetingRules(greetingRes.data.rules);
      if (assignmentRes.data.success) setAssignmentRules(assignmentRes.data.rules);
      if (qualificationRes.data.success) setQualificationRules(qualificationRes.data.rules);
      if (offlineRes.data.success) setOfflineRules(offlineRes.data.rules);
      if (websitesRes.data.success) setWebsites(websitesRes.data.websites);
      setTeams(teamsRes.data?.teams || teamsRes.data || []);
      setAgents(agentsRes.data?.users || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (type) => {
    setDialogType(type);
    setEditingRule(null);
    setFormData(getDefaultFormData(type));
    setShowDialog(true);
  };

  const openEditDialog = (type, rule) => {
    setDialogType(type);
    setEditingRule(rule);
    setFormData({ ...rule });
    setShowDialog(true);
  };

  const getDefaultFormData = (type) => {
    switch (type) {
      case 'greeting':
        return {
          name: '',
          website_id: '',
          is_active: true,
          trigger_type: 'time_on_page',
          trigger_config: { delay_seconds: 5, page_url_contains: '' },
          message: 'Hi! How can we help you today?',
          priority: 0
        };
      case 'assignment':
        return {
          name: '',
          website_id: '',
          is_active: true,
          priority: 0,
          conditions: [],
          assignment_type: 'round_robin',
          assignment_config: {}
        };
      case 'qualification':
        return {
          name: '',
          website_id: '',
          is_active: true,
          priority: 0,
          conditions: [],
          actions: [],
          auto_create_lead: false,
          lead_score_increment: 0
        };
      case 'offline':
        return {
          name: '',
          website_id: '',
          is_active: true,
          offline_message: "We're currently offline. Please leave your details and we'll get back to you.",
          required_fields: ['name', 'email'],
          optional_fields: ['phone', 'message'],
          notification_emails: [],
          auto_create_lead: true
        };
      default:
        return {};
    }
  };

  const handleSaveRule = async () => {
    if (!formData.name) {
      toast.error('Rule name is required');
      return;
    }

    const endpoints = {
      greeting: 'auto-greeting',
      assignment: 'auto-assignment',
      qualification: 'lead-qualification',
      offline: 'offline-capture'
    };

    try {
      const endpoint = endpoints[dialogType];
      
      // Clean up website_id
      const data = { ...formData };
      if (data.website_id === '' || data.website_id === 'all') {
        data.website_id = null;
      }
      
      if (editingRule) {
        const response = await axios.put(
          `${API}/api/live-chat/rules/${endpoint}/${editingRule.id}?token=${token}`,
          data
        );
        if (response.data.success) {
          toast.success('Rule updated');
        }
      } else {
        const response = await axios.post(
          `${API}/api/live-chat/rules/${endpoint}?token=${token}`,
          data
        );
        if (response.data.success) {
          toast.success('Rule created');
        }
      }
      
      setShowDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDeleteRule = async (type, ruleId) => {
    if (!confirm('Delete this rule?')) return;

    const endpoints = {
      greeting: 'auto-greeting',
      assignment: 'auto-assignment',
      qualification: 'lead-qualification',
      offline: 'offline-capture'
    };

    try {
      const response = await axios.delete(
        `${API}/api/live-chat/rules/${endpoints[type]}/${ruleId}?token=${token}`
      );
      if (response.data.success) {
        toast.success('Rule deleted');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const toggleRuleStatus = async (type, rule) => {
    const endpoints = {
      greeting: 'auto-greeting',
      assignment: 'auto-assignment',
      qualification: 'lead-qualification',
      offline: 'offline-capture'
    };

    try {
      await axios.put(
        `${API}/api/live-chat/rules/${endpoints[type]}/${rule.id}?token=${token}`,
        { is_active: !rule.is_active }
      );
      toast.success(rule.is_active ? 'Rule deactivated' : 'Rule activated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const RuleCard = ({ rule, type, icon: Icon, color }) => (
    <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-slate-900">{rule.name}</h4>
              <Badge variant={rule.is_active ? 'default' : 'secondary'} className={rule.is_active ? 'bg-emerald-50 text-emerald-700' : ''}>
                {rule.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            {type === 'greeting' && (
              <p className="text-sm text-slate-500">
                Trigger: {rule.trigger_type === 'time_on_page' ? `After ${rule.trigger_config?.delay_seconds || 5}s` : 'On page load'}
                {rule.trigger_config?.page_url_contains && ` on pages containing "${rule.trigger_config.page_url_contains}"`}
              </p>
            )}
            {type === 'assignment' && (
              <p className="text-sm text-slate-500">
                Assignment: {rule.assignment_type?.replace('_', ' ')}
              </p>
            )}
            {type === 'qualification' && (
              <p className="text-sm text-slate-500">
                {rule.auto_create_lead ? 'Auto-creates leads' : 'Score increment: +' + (rule.lead_score_increment || 0)}
              </p>
            )}
            {type === 'offline' && (
              <p className="text-sm text-slate-500">
                {rule.auto_create_lead ? 'Auto-creates leads from offline submissions' : 'Captures offline messages'}
              </p>
            )}
            {rule.website_id && (
              <p className="text-xs text-slate-400 mt-1">
                Website: {websites.find(w => w.id === rule.website_id)?.name || 'Unknown'}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => toggleRuleStatus(type, rule)}
            title={rule.is_active ? 'Deactivate' : 'Activate'}
          >
            {rule.is_active ? <Pause size={14} /> : <Play size={14} />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => openEditDialog(type, rule)}>
            <Edit2 size={14} />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => handleDeleteRule(type, rule.id)}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-chat-rules">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/live-chat')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Rules & Automation</h1>
            <p className="text-sm text-slate-500">Configure auto-greetings, assignments, and lead qualification</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="greeting" className="gap-2">
            <MessageSquare size={14} /> Auto Greeting
          </TabsTrigger>
          <TabsTrigger value="assignment" className="gap-2">
            <UserPlus size={14} /> Auto Assignment
          </TabsTrigger>
          <TabsTrigger value="qualification" className="gap-2">
            <Target size={14} /> Lead Qualification
          </TabsTrigger>
          <TabsTrigger value="offline" className="gap-2">
            <Clock size={14} /> Offline Capture
          </TabsTrigger>
        </TabsList>

        {/* Auto Greeting Rules */}
        <TabsContent value="greeting" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Automatically greet visitors based on their behavior
            </p>
            <Button onClick={() => openCreateDialog('greeting')} className="gap-2">
              <Plus size={16} /> Add Rule
            </Button>
          </div>
          
          {greetingRules.length === 0 ? (
            <Card className="p-8 text-center">
              <MessageSquare size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">No greeting rules configured</p>
              <Button variant="outline" onClick={() => openCreateDialog('greeting')}>
                Create First Rule
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {greetingRules.map(rule => (
                <RuleCard 
                  key={rule.id} 
                  rule={rule} 
                  type="greeting" 
                  icon={MessageSquare}
                  color="bg-blue-50 text-blue-600"
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Auto Assignment Rules */}
        <TabsContent value="assignment" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Automatically assign chats to agents or teams
            </p>
            <Button onClick={() => openCreateDialog('assignment')} className="gap-2">
              <Plus size={16} /> Add Rule
            </Button>
          </div>
          
          {assignmentRules.length === 0 ? (
            <Card className="p-8 text-center">
              <UserPlus size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">No assignment rules configured</p>
              <Button variant="outline" onClick={() => openCreateDialog('assignment')}>
                Create First Rule
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {assignmentRules.map(rule => (
                <RuleCard 
                  key={rule.id} 
                  rule={rule} 
                  type="assignment" 
                  icon={UserPlus}
                  color="bg-violet-50 text-violet-600"
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Lead Qualification Rules */}
        <TabsContent value="qualification" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Qualify visitors and auto-create leads based on behavior
            </p>
            <Button onClick={() => openCreateDialog('qualification')} className="gap-2">
              <Plus size={16} /> Add Rule
            </Button>
          </div>
          
          {qualificationRules.length === 0 ? (
            <Card className="p-8 text-center">
              <Target size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">No qualification rules configured</p>
              <Button variant="outline" onClick={() => openCreateDialog('qualification')}>
                Create First Rule
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {qualificationRules.map(rule => (
                <RuleCard 
                  key={rule.id} 
                  rule={rule} 
                  type="qualification" 
                  icon={Target}
                  color="bg-emerald-50 text-emerald-600"
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Offline Capture Rules */}
        <TabsContent value="offline" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Capture visitor details when agents are offline
            </p>
            <Button onClick={() => openCreateDialog('offline')} className="gap-2">
              <Plus size={16} /> Add Rule
            </Button>
          </div>
          
          {offlineRules.length === 0 ? (
            <Card className="p-8 text-center">
              <Clock size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">No offline capture rules configured</p>
              <Button variant="outline" onClick={() => openCreateDialog('offline')}>
                Create First Rule
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {offlineRules.map(rule => (
                <RuleCard 
                  key={rule.id} 
                  rule={rule} 
                  type="offline" 
                  icon={Clock}
                  color="bg-amber-50 text-amber-600"
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Rule Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? 'Edit' : 'Create'} {
                dialogType === 'greeting' ? 'Auto Greeting' :
                dialogType === 'assignment' ? 'Auto Assignment' :
                dialogType === 'qualification' ? 'Lead Qualification' :
                'Offline Capture'
              } Rule
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Common fields */}
            <div>
              <Label>Rule Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter rule name"
              />
            </div>

            <div>
              <Label>Website</Label>
              <Select 
                value={formData.website_id || 'all'} 
                onValueChange={(v) => setFormData({ ...formData, website_id: v === 'all' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All websites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Websites</SelectItem>
                  {websites.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Greeting-specific fields */}
            {dialogType === 'greeting' && (
              <>
                <div>
                  <Label>Trigger Type</Label>
                  <Select 
                    value={formData.trigger_type || 'time_on_page'} 
                    onValueChange={(v) => setFormData({ ...formData, trigger_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="page_load">On Page Load</SelectItem>
                      <SelectItem value="time_on_page">After Time on Page</SelectItem>
                      <SelectItem value="exit_intent">Exit Intent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.trigger_type === 'time_on_page' && (
                  <div>
                    <Label>Delay (seconds)</Label>
                    <Input
                      type="number"
                      value={formData.trigger_config?.delay_seconds || 5}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        trigger_config: { ...formData.trigger_config, delay_seconds: parseInt(e.target.value) || 5 }
                      })}
                    />
                  </div>
                )}

                <div>
                  <Label>Page URL Contains (optional)</Label>
                  <Input
                    value={formData.trigger_config?.page_url_contains || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      trigger_config: { ...formData.trigger_config, page_url_contains: e.target.value }
                    })}
                    placeholder="/pricing, /contact, etc."
                  />
                </div>

                <div>
                  <Label>Greeting Message *</Label>
                  <Textarea
                    value={formData.message || ''}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Hi! How can we help you today?"
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Assignment-specific fields */}
            {dialogType === 'assignment' && (
              <>
                <div>
                  <Label>Assignment Type</Label>
                  <Select 
                    value={formData.assignment_type || 'round_robin'} 
                    onValueChange={(v) => setFormData({ ...formData, assignment_type: v, assignment_config: {} })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="round_robin">Round Robin</SelectItem>
                      <SelectItem value="least_busy">Least Busy Agent</SelectItem>
                      <SelectItem value="specific_agent">Specific Agent</SelectItem>
                      <SelectItem value="specific_team">Specific Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.assignment_type === 'specific_agent' && (
                  <div>
                    <Label>Select Agent</Label>
                    <Select 
                      value={formData.assignment_config?.agent_id || ''} 
                      onValueChange={(v) => setFormData({ 
                        ...formData, 
                        assignment_config: { agent_id: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        {agents.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.full_name || a.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.assignment_type === 'specific_team' && (
                  <div>
                    <Label>Select Team</Label>
                    <Select 
                      value={formData.assignment_config?.team_id || ''} 
                      onValueChange={(v) => setFormData({ 
                        ...formData, 
                        assignment_config: { team_id: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* Qualification-specific fields */}
            {dialogType === 'qualification' && (
              <>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Auto-create Lead</p>
                    <p className="text-sm text-slate-500">Automatically create lead when conditions match</p>
                  </div>
                  <Switch
                    checked={formData.auto_create_lead || false}
                    onCheckedChange={(v) => setFormData({ ...formData, auto_create_lead: v })}
                  />
                </div>

                <div>
                  <Label>Lead Score Increment</Label>
                  <Input
                    type="number"
                    value={formData.lead_score_increment || 0}
                    onChange={(e) => setFormData({ ...formData, lead_score_increment: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <p className="text-xs text-slate-500 mt-1">Points to add to visitor's lead score</p>
                </div>
              </>
            )}

            {/* Offline-specific fields */}
            {dialogType === 'offline' && (
              <>
                <div>
                  <Label>Offline Message</Label>
                  <Textarea
                    value={formData.offline_message || ''}
                    onChange={(e) => setFormData({ ...formData, offline_message: e.target.value })}
                    placeholder="We're currently offline..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Auto-create Lead</p>
                    <p className="text-sm text-slate-500">Create lead from offline form submissions</p>
                  </div>
                  <Switch
                    checked={formData.auto_create_lead !== false}
                    onCheckedChange={(v) => setFormData({ ...formData, auto_create_lead: v })}
                  />
                </div>

                <div>
                  <Label>Notification Emails</Label>
                  <Input
                    value={(formData.notification_emails || []).join(', ')}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      notification_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="text-xs text-slate-500 mt-1">Comma-separated emails to notify</p>
                </div>
              </>
            )}

            {/* Priority */}
            <div>
              <Label>Priority</Label>
              <Input
                type="number"
                value={formData.priority || 0}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
              <p className="text-xs text-slate-500 mt-1">Higher priority rules are evaluated first</p>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Active</p>
                <p className="text-sm text-slate-500">Enable this rule</p>
              </div>
              <Switch
                checked={formData.is_active !== false}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRule}>
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveChatRules;
